<template>
  <header class="sticky top-0 z-30 border-b border-orange-100/70 bg-white/95 shadow-[0_4px_22px_rgba(11,33,73,.04)] backdrop-blur-xl">
    <div class="nav-transition mx-auto max-w-[1600px] px-3 sm:px-4 md:px-5 lg:px-8">
      <div class="flex flex-col gap-2 py-2.5 sm:gap-3 sm:py-3 xl:grid xl:grid-cols-[auto_minmax(0,1fr)_auto] xl:items-center xl:gap-6 xl:pb-3 xl:pt-4">
        <div class="order-1 flex min-w-0 items-center justify-between gap-2 xl:block">
          <RouterLink to="/" class="min-w-0 shrink-0 text-lg font-semibold text-ink"><img class="navbar-logo" src="@/assets/logo.png" alt="Invi8 Logo"></RouterLink>  
          <div class="flex shrink-0 items-center gap-1.5 sm:gap-2 xl:hidden">
            <div v-if="isLoggedIn" class="relative flex items-center">
              <button
                ref="mobileProfileButtonRef"
                type="button"
                class="mobile-icon-btn mobile-profile-btn"
                @click.stop="profileMenuOpen = !profileMenuOpen"
              >
                <span class="flex h-8 w-8 items-center justify-center rounded-full bg-[#FFE5EC] text-brand">
                  <i class="ri-user-3-line text-base"></i>
                </span>
                <i class="ri-arrow-down-s-line text-base text-muted"></i>
              </button>
              <transition name="fade">
                <div
                  v-if="profileMenuOpen"
                  ref="mobileProfileDropdownRef"
                  class="absolute right-0 top-full mt-3 w-56 rounded-2xl border border-gray-100 bg-white p-3 shadow-[0_18px_40px_rgba(0,0,0,0.12)] z-40"
                >
                  <div class="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-muted">Account</div>
                  <RouterLink class="flex items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-ink transition hover:bg-gray-50" :to="accountHomeLink">
                    <span>{{ accountHomeLabel }}</span>
                    <i class="ri-dashboard-line text-sm text-muted"></i>
                  </RouterLink>
                  <RouterLink class="flex items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-ink transition hover:bg-gray-50" :to="accountSettingsLink">
                    <span>Settings</span>
                    <i class="ri-settings-3-line text-sm text-muted"></i>
                  </RouterLink>
                  <button
                    type="button"
                    class="mt-1 flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-ink transition hover:bg-gray-50"
                    @click="handleLogout"
                  >
                    <span>Logout</span>
                    <i class="ri-logout-box-r-line text-sm text-muted"></i>
                  </button>
                </div>
              </transition>
            </div>
            <RouterLink
              v-else
              class="mobile-icon-btn"
              to="/login"
              aria-label="Log in"
            >
              <i class="ri-user-line text-lg"></i>
            </RouterLink>
            <button v-if="!hideDashboardExtras" class="mobile-icon-btn" aria-label="Open menu" @click="ui.toggleSidenav">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        <div class="order-3 flex min-w-0 items-center xl:order-2 xl:justify-center">
          <button
            v-if="showSearch"
            type="button"
            class="group flex min-w-0 max-w-full items-center gap-2 px-1 py-1.5 text-left xl:hidden"
            aria-haspopup="dialog"
            @click="openLocationPicker"
          >
            <i class="ri-map-pin-2-fill shrink-0 text-lg text-brand"></i>
            <span class="truncate text-sm font-semibold" :class="locationSearchQuery ? 'text-ink' : 'text-muted'">{{ locationSearchQuery || 'Select location' }}</span>
            <i class="ri-arrow-down-s-line shrink-0 text-base text-muted transition group-hover:text-brand"></i>
          </button>
          <nav v-if="!hideDashboardExtras" class="hidden items-center justify-center gap-7 xl:flex" aria-label="Primary navigation">
            <RouterLink v-for="link in primaryNavLinks" :key="link.to" :to="link.to" class="primary-nav-link relative text-sm font-semibold text-muted transition hover:text-brand" active-class="text-brand">
              {{ link.label }}
            </RouterLink>
          </nav>
        </div>

        <div class="order-2 hidden items-center justify-end gap-3 self-end xl:flex xl:order-3 xl:self-center">
          <button
            v-if="showSearch"
            type="button"
            class="group inline-flex max-w-[220px] items-center gap-2 px-1 py-2 text-sm font-semibold text-ink transition hover:text-brand"
            aria-haspopup="dialog"
            @click="openLocationPicker"
          >
            <i class="ri-map-pin-2-fill shrink-0 text-lg text-brand"></i>
            <span class="truncate">{{ locationSearchQuery || 'Select location' }}</span>
            <i class="ri-arrow-down-s-line shrink-0 text-base text-muted transition group-hover:text-brand"></i>
          </button>
          <span v-if="showSearch" class="h-6 w-px bg-slate-200"></span>
          <RouterLink v-if="!isLoggedIn && !hideDashboardExtras" class="rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white shadow-[0_8px_20px_rgba(255,122,0,.2)] transition hover:-translate-y-0.5 hover:bg-brandDark" to="/vendor-signup?role=vendor">Become a Vendor</RouterLink> 
          <div v-if="isLoggedIn" class="relative flex items-center">
            <button
              ref="desktopProfileButtonRef"
              type="button"
              class="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-2.5 py-1.5 text-sm font-semibold text-ink shadow-sm transition hover:border-gray-300"
              @click.stop="profileMenuOpen = !profileMenuOpen"
            >
              <span class="flex h-8 w-8 items-center justify-center rounded-full bg-[#FFE5EC] text-brand">
                <i class="ri-user-3-line text-base"></i>
              </span>
              <span class="hidden xl:inline">{{ displayName }}</span>
              <i class="ri-arrow-down-s-line text-base text-muted"></i>
            </button>
            <transition name="fade">
              <div
                v-if="profileMenuOpen"
                ref="desktopProfileDropdownRef"
                class="absolute right-0 top-full mt-3 w-56 rounded-2xl border border-gray-100 bg-white p-3 shadow-[0_18px_40px_rgba(0,0,0,0.12)] z-40"
              >
                <div class="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-muted">Account</div>
                <RouterLink class="flex items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-ink transition hover:bg-gray-50" :to="accountHomeLink">
                  <span>{{ accountHomeLabel }}</span>
                  <i class="ri-dashboard-line text-sm text-muted"></i>
                </RouterLink>
                <RouterLink class="flex items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-ink transition hover:bg-gray-50" :to="accountSettingsLink">
                  <span>Settings</span>
                  <i class="ri-settings-3-line text-sm text-muted"></i>
                </RouterLink>
                <button
                  type="button"
                  class="mt-1 flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-ink transition hover:bg-gray-50"
                  @click="handleLogout"
                >
                  <span>Logout</span>
                  <i class="ri-logout-box-r-line text-sm text-muted"></i>
                </button>
              </div>
            </transition>
          </div>
          <RouterLink
            v-else
            class="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-ink shadow-sm transition hover:border-gray-300"
            to="/login"
            aria-label="Log in"
          >
            <i class="ri-user-line text-lg"></i>
          </RouterLink>
        </div>
      </div>
    </div>
  </header>
  <Teleport to="body">
    <transition name="fade">
      <div
        v-if="locationSearchOpen"
        class="fixed inset-0 z-[120] overflow-y-auto bg-ink/65 backdrop-blur-md"
        role="dialog"
        aria-modal="true"
        aria-labelledby="location-modal-title"
        @click.self="closeLocationPicker"
      >
        <div class="flex min-h-screen items-start justify-center p-3 sm:items-center sm:p-6">
          <div class="w-full max-w-5xl overflow-hidden rounded-[28px] border border-white/30 bg-[#FFFCF9] shadow-[0_32px_100px_rgba(11,33,73,.28)] sm:rounded-[34px]">
            <div class="flex items-center justify-between gap-4 border-b border-orange-100 bg-white px-5 py-4 sm:px-8 sm:py-5">
              <div class="flex items-center gap-3">
                <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-orange-50 text-brand">
                  <i class="ri-map-pin-2-fill text-xl"></i>
                </span>
                <div>
                  <p class="text-[10px] font-bold uppercase tracking-[0.2em] text-brand">Explore nearby</p>
                  <h3 id="location-modal-title" class="mt-0.5 text-lg font-bold text-ink sm:text-xl">Where are you planning?</h3>
                </div>
              </div>
              <button
                type="button"
                class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-ink transition hover:border-brand hover:bg-orange-50 hover:text-brand"
                aria-label="Close search"
                @click="closeLocationPicker"
              >
                <i class="ri-close-line text-xl"></i>
              </button>
            </div>

            <div class="max-h-[calc(100vh-1.5rem-77px)] overflow-y-auto sm:max-h-[calc(100vh-5rem)]">
              <div class="grid lg:grid-cols-[minmax(0,1.05fr)_minmax(340px,.95fr)]">
                <div class="min-w-0 p-5 sm:p-8">
                  <div>
                    <p class="text-xs font-bold text-ink"><span class="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-ink text-[11px] text-white">1</span>What are you looking for?</p>
                    <div class="mt-3 grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        class="flex items-center gap-3 rounded-2xl border p-3 text-left transition sm:p-4"
                        :class="searchType === 'venues' ? 'border-brand bg-orange-50 text-ink shadow-[0_6px_18px_rgba(255,122,0,.08)]' : 'border-slate-200 bg-white text-ink hover:border-orange-200'"
                        @click="searchType = 'venues'"
                      >
                        <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" :class="searchType === 'venues' ? 'bg-brand text-white' : 'bg-slate-100 text-slate-500'"><i class="ri-building-2-line text-lg"></i></span>
                        <span><span class="block text-sm font-bold">Venues</span><span class="mt-0.5 hidden text-xs text-muted sm:block">Spaces for your event</span></span>
                        <i v-if="searchType === 'venues'" class="ri-checkbox-circle-fill ml-auto text-lg text-brand"></i>
                      </button>
                      <button
                        type="button"
                        class="flex items-center gap-3 rounded-2xl border p-3 text-left transition sm:p-4"
                        :class="searchType === 'vendors' ? 'border-brand bg-orange-50 text-ink shadow-[0_6px_18px_rgba(255,122,0,.08)]' : 'border-slate-200 bg-white text-ink hover:border-orange-200'"
                        @click="searchType = 'vendors'"
                      >
                        <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" :class="searchType === 'vendors' ? 'bg-brand text-white' : 'bg-slate-100 text-slate-500'"><i class="ri-store-2-line text-lg"></i></span>
                        <span><span class="block text-sm font-bold">Vendors</span><span class="mt-0.5 hidden text-xs text-muted sm:block">People and services</span></span>
                        <i v-if="searchType === 'vendors'" class="ri-checkbox-circle-fill ml-auto text-lg text-brand"></i>
                      </button>
                    </div>
                  </div>

                  <div class="mt-6">
                    <label for="modal-location-search" class="text-xs font-bold text-ink"><span class="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-ink text-[11px] text-white">2</span>Choose a location</label>
                    <div class="relative mt-3">
                      <i class="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-xl text-slate-400"></i>
                    <input
                      ref="modalSearchInputRef"
                      id="modal-location-search"
                      v-model.trim="locationSearchQuery"
                      type="search"
                      autocomplete="off"
                      placeholder="Search city, neighbourhood, or address"
                      class="h-14 w-full rounded-2xl border border-slate-200 bg-white pl-12 pr-4 text-sm font-medium text-ink outline-none transition placeholder:text-slate-400 focus:border-brand focus:ring-4 focus:ring-orange-100"
                      @input="searchNavbarLocation"
                    >
                  </div>

                  <button
                    type="button"
                    class="mt-3 flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left transition hover:border-brand hover:bg-orange-50 disabled:cursor-wait disabled:opacity-60"
                    :disabled="locationCurrentLoading"
                    @click="useNavbarCurrentLocation"
                  >
                    <span class="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 text-brand">
                      <i class="ri-navigation-line text-lg"></i>
                    </span>
                    <span class="min-w-0 flex-1">
                      <span class="block text-sm font-bold text-ink">{{ locationCurrentLoading ? 'Finding your location…' : 'Use my current location' }}</span>
                      <span class="mt-0.5 block text-xs text-muted">Uses your device location with permission</span>
                    </span>
                    <i class="ri-arrow-right-s-line text-xl text-slate-400"></i>
                  </button>
                  </div>

                  <div class="mt-5">
                    <div v-if="locationSearchResults.length" class="mb-2 flex items-center justify-between px-1"><p class="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">Suggested locations</p><span class="text-xs text-muted">{{ locationSearchResults.length }} results</span></div>
                    <div class="max-h-[235px] space-y-1 overflow-y-auto pr-1">
                    <button
                      v-for="(item, index) in locationSearchResults"
                      :key="`${item.placeId || item.label}-${index}`"
                      type="button"
                      class="group flex w-full items-start gap-3 rounded-2xl border border-transparent px-3 py-3 text-left transition hover:border-orange-100 hover:bg-white hover:shadow-sm"
                      @click="selectNavbarLocation(item)"
                    >
                      <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-ink transition group-hover:bg-orange-50 group-hover:text-brand">
                        <i class="ri-map-pin-2-line text-lg"></i>
                      </span>
                      <span class="min-w-0 flex-1">
                        <span class="block text-sm font-bold text-ink">{{ item.label }}</span>
                        <span class="mt-0.5 block text-xs text-muted">{{ item.meta }}</span>
                      </span>
                      <i class="ri-arrow-right-s-line mt-2 text-lg text-slate-300 transition group-hover:text-brand"></i>
                    </button>

                    <div v-if="locationSearchLoading" class="flex items-center gap-3 rounded-2xl bg-white px-4 py-4 text-sm text-muted"><span class="h-5 w-5 animate-spin rounded-full border-2 border-orange-200 border-t-brand"></span>Searching locations…</div>
                    <div v-else-if="locationPickerError" class="flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50 px-4 py-4 text-sm text-red-600"><i class="ri-error-warning-line mt-0.5"></i><span>{{ locationPickerError }}</span></div>
                    <div v-else-if="!locationSearchResults.length" class="rounded-2xl border border-dashed border-orange-200 bg-orange-50/50 px-4 py-4 text-center"><i class="ri-map-pin-search-line text-2xl text-brand"></i><p class="mt-1 text-sm font-semibold text-ink">Start typing a location</p><p class="mt-1 text-xs text-muted">Enter at least 3 characters to see suggestions.</p></div>
                    </div>
                  </div>
                </div>

                <div class="flex min-h-[420px] flex-col border-t border-orange-100 bg-white p-5 sm:p-8 lg:border-l lg:border-t-0">
                  <div class="flex items-center justify-between">
                    <p class="text-xs font-bold text-ink"><span class="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-ink text-[11px] text-white">3</span>Confirm on the map</p>
                    <span v-if="locationSearchQuery" class="rounded-full bg-green-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-green-700">Selected</span>
                  </div>
                  <div class="relative mt-3 flex-1 overflow-hidden rounded-[24px] border border-slate-200 bg-slate-100">
                    <div ref="modalMapRef" class="h-[260px] w-full sm:h-[330px] lg:h-full lg:min-h-[330px]"></div>
                    <div class="pointer-events-none absolute inset-x-3 bottom-3 rounded-2xl border border-white/70 bg-white/95 p-3 shadow-[0_12px_30px_rgba(11,33,73,.16)] backdrop-blur">
                      <div class="flex items-start gap-3">
                        <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-brand"><i class="ri-map-pin-fill"></i></span>
                        <div class="min-w-0"><p class="truncate text-sm font-bold text-ink">{{ locationSearchQuery || 'No location selected' }}</p><p class="mt-0.5 text-xs text-muted">We’ll show {{ searchType === 'vendors' ? 'vendors' : 'venues' }} around this area.</p></div>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    class="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-brand px-5 py-3.5 text-sm font-bold text-white shadow-[0_10px_24px_rgba(255,122,0,.24)] transition hover:-translate-y-0.5 hover:bg-brandDark disabled:cursor-not-allowed disabled:opacity-50"
                    :disabled="!locationSearchQuery"
                    @click="submitNavbarSearch"
                  >
                    <i class="ri-search-line text-lg"></i>
                    Show {{ searchType === 'vendors' ? 'vendors' : 'venues' }} nearby
                  </button>
                  <p class="mt-2 text-center text-[11px] text-muted">You can change this location anytime.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import { useUiStore } from '@/stores/ui'
import { useSearchStore } from '@/stores/search'
import { clearAuth, getAuthRole, getAuthUser, isAuthenticated } from '@/services/auth'

const GOOGLE_API_KEY = 'AIzaSyDOAEexenU_AutS0ULOGzNa3HQZHpikt4A'
const NAVBAR_SEARCH_STORAGE_KEY = 'invi8_frontend_navbar_search'

const ui = useUiStore()
const searchStore = useSearchStore()
const route = useRoute()
const router = useRouter()

L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  shadowUrl: markerShadow
})

const profileMenuOpen = ref(false)
const mobileProfileDropdownRef = ref(null)
const mobileProfileButtonRef = ref(null)
const desktopProfileDropdownRef = ref(null)
const desktopProfileButtonRef = ref(null)
const searchLocation = ref(String(route.query.location || ''))
const locationSearchQuery = ref(String(route.query.location || ''))
const locationSearchResults = ref([])
const locationSearchLoading = ref(false)
const locationCurrentLoading = ref(false)
const locationPickerError = ref('')
const locationSearchOpen = ref(false)
const modalSearchInputRef = ref(null)
const modalMapRef = ref(null)
const searchLat = ref(String(route.query.lat || ''))
const searchLng = ref(String(route.query.lng || ''))
const searchType = ref('venues')
const googleReady = ref(false)
const googleScriptLoading = ref(false)
const autocompleteService = ref(null)
const placesService = ref(null)
const geocoder = ref(null)
const modalMap = ref(null)
const modalMapMarker = ref(null)

const hasStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'

const getStoredNavbarSearch = () => {
  if (!hasStorage()) return null
  const raw = localStorage.getItem(NAVBAR_SEARCH_STORAGE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

const persistNavbarSearch = () => {
  if (!hasStorage()) return
  localStorage.setItem(
    NAVBAR_SEARCH_STORAGE_KEY,
    JSON.stringify({
      type: searchType.value || 'venues',
      location: searchLocation.value || locationSearchQuery.value || '',
      lat: searchLat.value || '',
      lng: searchLng.value || ''
    })
  )
}

const parseCoordinate = (value) => {
  const parsed = Number(String(value ?? '').trim())
  return Number.isFinite(parsed) ? parsed : null
}

const syncSearchStore = ({ text, lat, lng }) => {
  searchStore.setLocation({
    text: text || '',
    lat: lat === '' || lat === null || lat === undefined ? null : Number(lat),
    lng: lng === '' || lng === null || lng === undefined ? null : Number(lng)
  })
}

const primaryNavLinks = [
  { to: '/', label: 'Home' },
  { to: '/venues', label: 'Venues' },
  { to: '/vendors', label: 'Vendors' },
  { to: '/for-users', label: 'For Users' },
  { to: '/for-vendors', label: 'For Vendors' },
  { to: '/use-cases', label: 'How it works' },
  { to: '/about', label: 'About' }
]
 
const isVendorDetail = computed(() => (router.currentRoute.value.path || '').startsWith('/vendor/'))
const currentPath = computed(() => router.currentRoute.value.path || '')
const currentName = computed(() => router.currentRoute.value.name || '')
const isDashboardRoute = computed(() => currentPath.value.toLowerCase().startsWith('/dashboard'))

const hiddenPaths = ['/vendor-signup', '/vendor-onboard', '/login', '/register', '/forgot-password', '/dashboard', '/404', '/not-found']
const hiddenNames = ['VendorSignup', 'VendorOnboard', 'Login', 'Register', 'ForgotPassword', 'Dashboard', 'DashboardVenues', 'DashboardPackages', 'DashboardSettings', 'NotFound']

const hideNavUi = computed(() => {
  const path = currentPath.value.toLowerCase()
  if (isVendorDetail.value) return true
  if (hiddenPaths.some((p) => path.startsWith(p))) return true
  const name = (currentName.value || '').toString().toLowerCase()
  return hiddenNames.map((n) => n.toLowerCase()).includes(name)
})

const showSearch = computed(() => !hideNavUi.value)
const hideDashboardExtras = computed(() => isLoggedIn.value && isDashboardRoute.value)
const navCollapsed = ref(false)
const forceExpanded = ref(false)
const forceExpandedAt = ref(0)

const handleClickOutside = (event) => {
  const target = event.target
  if (profileMenuOpen.value) {
    if (mobileProfileDropdownRef.value && mobileProfileDropdownRef.value.contains(target)) return
    if (mobileProfileButtonRef.value && mobileProfileButtonRef.value.contains(target)) return
    if (desktopProfileDropdownRef.value && desktopProfileDropdownRef.value.contains(target)) return
    if (desktopProfileButtonRef.value && desktopProfileButtonRef.value.contains(target)) return
    profileMenuOpen.value = false
  }
}

const handleEsc = (event) => {
  if (event.key === 'Escape') {
    closeLocationPicker()
    profileMenuOpen.value = false
  }
}

const collapseThreshold = 80
const handleScroll = () => {
  const y = window.scrollY || document.documentElement.scrollTop
  const nearTop = y < 12

  if (nearTop) {
    navCollapsed.value = false
    forceExpanded.value = false
    return
  }

  if (forceExpanded.value) {
    const moved = Math.abs(y - forceExpandedAt.value) > 50
    if (moved && y > forceExpandedAt.value) {
      forceExpanded.value = false
    } else {
      navCollapsed.value = false
      return
    }
  }

  navCollapsed.value = y > collapseThreshold
}

const getSearchTypeFromRoute = () => {
  const queryType = String(route.query.type || '').toLowerCase()
  if (queryType === 'vendors' || queryType === 'vendor') return 'vendors'
  if (queryType === 'venues' || queryType === 'venue') return 'venues'

  const storedType = String(getStoredNavbarSearch()?.type || '').toLowerCase()
  if (storedType === 'vendors' || storedType === 'vendor') return 'vendors'
  if (storedType === 'venues' || storedType === 'venue') return 'venues'

  const path = currentPath.value.toLowerCase()
  if (path.startsWith('/vendors')) return 'vendors'
  if (path.startsWith('/venues')) return 'venues'
  return 'venues'
}

const initGooglePlacesServices = () => {
  if (typeof window === 'undefined' || !window.google?.maps?.places) return false
  googleReady.value = true
  autocompleteService.value = autocompleteService.value || new window.google.maps.places.AutocompleteService()
  placesService.value = placesService.value || new window.google.maps.places.PlacesService(document.createElement('div'))
  geocoder.value = geocoder.value || new window.google.maps.Geocoder()
  googleScriptLoading.value = false
  return true
}

const ensureGooglePlaces = () => {
  if (initGooglePlacesServices()) return Promise.resolve(true)
  if (!GOOGLE_API_KEY) return Promise.resolve(false)

  return new Promise((resolve) => {
    const existingScript = document.querySelector('script[data-google-maps="places"], script[src*="maps.googleapis.com/maps/api/js"]')
    if (existingScript) {
      if (googleScriptLoading.value) {
        existingScript.addEventListener('load', () => resolve(initGooglePlacesServices()), { once: true })
        existingScript.addEventListener('error', () => resolve(false), { once: true })
        return
      }
      resolve(initGooglePlacesServices())
      return
    }

    googleScriptLoading.value = true
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`
    script.async = true
    script.defer = true
    script.dataset.googleMaps = 'places'
    script.onload = () => resolve(initGooglePlacesServices())
    script.onerror = () => {
      googleScriptLoading.value = false
      resolve(false)
    }
    document.head.appendChild(script)
  })
}

const openLocationPicker = () => {
  locationSearchOpen.value = true
  locationPickerError.value = ''
  nextTick(() => {
    modalSearchInputRef.value?.focus?.()
    ensureModalMap()
    updateModalMapPosition()
  })
  ensureGooglePlaces().then((loaded) => {
    if (!loaded && !locationSearchQuery.value.trim()) {
      locationPickerError.value = 'Google location search is unavailable right now.'
    }
  })
}

const closeLocationPicker = () => {
  locationSearchOpen.value = false
}

const ensureModalMap = () => {
  if (modalMap.value || !modalMapRef.value) return
  modalMap.value = L.map(modalMapRef.value, {
    zoomControl: true,
    attributionControl: true
  }).setView([20.5937, 78.9629], 5)

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap contributors'
  }).addTo(modalMap.value)
}

const updateModalMapPosition = () => {
  if (!modalMap.value) return

  const lat = parseCoordinate(searchLat.value)
  const lng = parseCoordinate(searchLng.value)
  const hasCoords = lat !== null && lng !== null

  if (!hasCoords) {
    if (modalMapMarker.value) {
      modalMap.value.removeLayer(modalMapMarker.value)
      modalMapMarker.value = null
    }
    modalMap.value.setView([20.5937, 78.9629], 5)
    modalMap.value.invalidateSize()
    return
  }

  if (!modalMapMarker.value) {
    modalMapMarker.value = L.marker([lat, lng]).addTo(modalMap.value)
  } else {
    modalMapMarker.value.setLatLng([lat, lng])
  }

  modalMapMarker.value.bindPopup(locationSearchQuery.value || searchLocation.value || 'Selected location')
  modalMap.value.setView([lat, lng], 14)
  modalMap.value.invalidateSize()
}

const searchNavbarLocation = async () => {
  const query = locationSearchQuery.value.trim()
  searchLocation.value = query
  searchLat.value = ''
  searchLng.value = ''
  locationSearchOpen.value = true

  if (query.length < 3) {
    locationSearchResults.value = []
    locationPickerError.value = 'Enter at least 3 characters to search.'
    return
  }

  locationPickerError.value = ''
  locationSearchLoading.value = true
  try {
    const loaded = await ensureGooglePlaces()
    if (!loaded || !autocompleteService.value) {
      throw new Error('google-places-unavailable')
    }

    locationSearchResults.value = await new Promise((resolve, reject) => {
      autocompleteService.value.getPlacePredictions(
        {
          input: query
        },
        (predictions, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !Array.isArray(predictions)) {
            if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              resolve([])
              return
            }
            reject(new Error(status || 'places-search-failed'))
            return
          }

          resolve(
            predictions.slice(0, 6).map((item) => ({
              placeId: item.place_id || '',
              label: item.structured_formatting?.main_text || item.description || query,
              meta: item.structured_formatting?.secondary_text || item.description || '',
              latitude: '',
              longitude: ''
            }))
          )
        }
      )
    })

    if (!locationSearchResults.value.length) {
      locationPickerError.value = 'No matching locations found.'
    }
  } catch {
    locationSearchResults.value = []
    locationPickerError.value = 'Unable to search locations right now.'
  } finally {
    locationSearchLoading.value = false
  }
}

const resolveNavbarPlaceDetails = (placeId) => {
  if (!placeId || !placesService.value || typeof window === 'undefined' || !window.google?.maps?.places) {
    return Promise.resolve(null)
  }

  return new Promise((resolve) => {
    placesService.value.getDetails(
      {
        placeId,
        fields: ['formatted_address', 'geometry.location', 'name']
      },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          resolve(null)
          return
        }

        resolve({
          label: place.formatted_address || place.name || '',
          latitude: place.geometry?.location?.lat?.() ?? '',
          longitude: place.geometry?.location?.lng?.() ?? ''
        })
      }
    )
  })
}

const selectNavbarLocation = async (item) => {
  if (!item?.label) return
  const placeDetails = item.placeId ? await resolveNavbarPlaceDetails(item.placeId) : null
  const resolvedLabel = placeDetails?.label || item.label
  searchLocation.value = resolvedLabel
  locationSearchQuery.value = resolvedLabel
  searchLat.value = String(placeDetails?.latitude || item.latitude || '')
  searchLng.value = String(placeDetails?.longitude || item.longitude || '')
  locationPickerError.value = ''
  syncSearchStore({
    text: resolvedLabel,
    lat: searchLat.value,
    lng: searchLng.value
  })
  persistNavbarSearch()
}

const reverseGeocodeNavbarLocation = async (latitude, longitude) => {
  const loaded = await ensureGooglePlaces()
  if (!loaded || !geocoder.value) {
    throw new Error('google-geocoder-unavailable')
  }

  const result = await geocoder.value.geocode({
    location: {
      lat: Number(latitude),
      lng: Number(longitude)
    }
  })

  return String(result?.results?.[0]?.formatted_address || 'Current location').trim()
}

const useNavbarCurrentLocation = async () => {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    locationPickerError.value = 'Geolocation is not supported in this browser.'
    return
  }

  locationPickerError.value = ''
  locationCurrentLoading.value = true
  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000
      })
    })
    const latitude = position.coords.latitude
    const longitude = position.coords.longitude
    const label = await reverseGeocodeNavbarLocation(latitude, longitude)
    searchLocation.value = label
    locationSearchQuery.value = label
    searchLat.value = String(latitude)
    searchLng.value = String(longitude)
    syncSearchStore({
      text: label,
      lat: latitude,
      lng: longitude
    })
    persistNavbarSearch()
  } catch {
    locationPickerError.value = 'Unable to detect your current location.'
  } finally {
    locationCurrentLoading.value = false
  }
}

const submitNavbarSearch = () => {
  searchLocation.value = locationSearchQuery.value || searchLocation.value
  syncSearchStore({
    text: searchLocation.value,
    lat: searchLat.value,
    lng: searchLng.value
  })
  persistNavbarSearch()
  closeLocationPicker()
  router.push({
    path: searchType.value === 'vendors' ? '/vendors' : '/venues',
    query: {
      type: searchType.value,
      location: searchLocation.value || undefined,
      lat: searchLat.value || undefined,
      lng: searchLng.value || undefined
    }
  })
}

const bootstrapDefaultLocation = async () => {
  const hasSelectedLocation =
    Boolean(route.query.location) ||
    Boolean(route.query.lat || route.query.lng) ||
    Boolean(getStoredNavbarSearch()?.location) ||
    Boolean(getStoredNavbarSearch()?.lat || getStoredNavbarSearch()?.lng) ||
    Boolean(searchStore.locationText) ||
    Boolean(searchStore.lat || searchStore.lng)

  if (hasSelectedLocation) return
  await useNavbarCurrentLocation()
}

const isLoggedIn = ref(isAuthenticated())
const userProfile = ref(getAuthUser())
const authRole = ref(getAuthRole() || '')

const refreshAuthState = () => {
  isLoggedIn.value = isAuthenticated()
  userProfile.value = getAuthUser()
  authRole.value = getAuthRole() || ''
}

const accountHomeLink = computed(() => (authRole.value === 'vendor' ? '/dashboard' : '/host'))
const accountHomeLabel = computed(() => (authRole.value === 'vendor' ? 'Dashboard' : 'My Panel'))
const accountSettingsLink = computed(() => (authRole.value === 'vendor' ? '/dashboard/settings' : '/settings'))

const displayName = computed(() => {
  const user = userProfile.value || {}
  return (
    user.name ||
    user.full_name ||
    user.fullName ||
    user.username ||
    user.email ||
    'Account'
  )
})

const handleLogout = () => {
  clearAuth()
  refreshAuthState()
  router.push('/login')
}

watch(
  () => route.fullPath,
  () => {
    const storedSearch = getStoredNavbarSearch() || {}
    searchType.value = getSearchTypeFromRoute()
    searchLocation.value = String(route.query.location || storedSearch.location || '')
    locationSearchQuery.value = String(route.query.location || storedSearch.location || '')
    searchLat.value = String(route.query.lat || storedSearch.lat || '')
    searchLng.value = String(route.query.lng || storedSearch.lng || '')
    syncSearchStore({
      text: searchLocation.value,
      lat: searchLat.value,
      lng: searchLng.value
    })
    refreshAuthState()
  },
  { immediate: true }
)

watch(
  () => [locationSearchOpen.value, searchLat.value, searchLng.value, locationSearchQuery.value],
  () => {
    if (!locationSearchOpen.value) return
    nextTick(() => {
      ensureModalMap()
      updateModalMapPosition()
    })
  }
)

const removeAfterEach = router.afterEach(() => {
  profileMenuOpen.value = false
})

onMounted(() => {
  window.addEventListener('click', handleClickOutside)
  window.addEventListener('keydown', handleEsc)
  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll()
  refreshAuthState()
  bootstrapDefaultLocation()
})

onUnmounted(() => {
  window.removeEventListener('click', handleClickOutside)
  window.removeEventListener('keydown', handleEsc)
  window.removeEventListener('scroll', handleScroll)
  if (modalMap.value) {
    modalMap.value.remove()
    modalMap.value = null
    modalMapMarker.value = null
  }
  if (typeof removeAfterEach === 'function') removeAfterEach()
})
</script>

<style scoped>
.nav-transition {
  transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes bounceSlow {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-2px); }
}

.animate-bounce-slow {
  animation: bounceSlow 1.2s infinite;
}

.nav-transition .primary-nav-link.router-link-active {
  color: var(--accent, #ec2263);
}

.pill-badge {
  padding: 2px 8px;
  border-radius: 12px;
  background: #1f2d3d;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
}

.mobile-icon-btn {
  display: inline-flex;
  height: 38px;
  min-width: 38px;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #fff;
  color: #1f2937;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
}

.mobile-profile-btn {
  gap: 4px;
  padding: 0 7px 0 5px;
}

.navbar-logo {
  display: block;
  height: 34px;
  width: auto;
}

@media (min-width: 640px) {
  .navbar-logo {
    height: 38px;
  }
  .mobile-icon-btn {
    height: 40px;
    min-width: 40px;
  }
}

@media (min-width: 1024px) {
  .navbar-logo {
    height: 40px;
  }
}
</style>
